import React from 'react';
import { useAuthStore } from '../../store/authStore';
import { useTranslation } from 'react-i18next';
import GlobalNavBar from '../layout/GlobalNavBar';
import { AdminSidebar } from './AdminSidebar';

interface AdminLayoutProps {
  children: React.ReactNode;
}

/**
 * Layout for admin pages. Renders the same global nav bar as the main layout
 * but swaps the side nav for `AdminSidebar`. Used by every route under
 * `/admin/*` so admins get a consistent admin-only navigation while on admin
 * pages.
 */
export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { user } = useAuthStore();
  const { t } = useTranslation('common');

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNavBar variant="dark" />

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:top-16 lg:bottom-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-1 min-h-0 bg-white border-r border-gray-200">
          <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto">
            <AdminSidebar variant="embedded" />
          </nav>
          <div className="px-4 py-4 border-t">
            <div className="flex items-center space-x-3">
              <img
                src={user?.profileImage || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(user?.name || 'Admin') + '&background=6366f1&color=fff&size=40'}
                alt={user?.name}
                className="h-10 w-10 rounded-full"
              />
              <div>
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500">
                  {user?.userType
                    ? t(`userTypeLabels.${user.userType}`, { defaultValue: user.userType })
                    : ''}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        <main className="flex-1">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
